import { Menu } from 'lucide-react'
import type { ReactNode } from 'react'

import type { ListFilterBarProps } from '@/components/common/ListFilterBar'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListFilterChips } from '@/components/common/list-filter-chips'
import { Button } from '@/components/ui/button'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

type PanelRender<T> = NonNullable<ListFilterBarProps<T>['children']>

const PANEL_ID = 'list-filter-sidebar-panel'

interface Props<T> {
    bar: ListFilterBarProps<T>
    /** Nội dung bộ lọc — cùng một hàm cho sidebar (áp ngay) và sheet mobile. */
    panel: PanelRender<T>
    /** Áp bộ lọc từ sidebar; mặc định `bar.onApply`. */
    onSidebarApply?: (next: T) => void
    /**
     * Cột lọc desktop đang thu gọn? Chỉ có tác dụng khi có
     * `onCollapsedChange`; không truyền thì cột lọc luôn mở như trước.
     */
    collapsed?: boolean
    /**
     * Bật/tắt thu gọn. Không truyền → KHÔNG render nút 3 ngạch ngang: màn chưa
     * chọn nơi lưu trạng thái thì thà không có nút còn hơn có một nút bấm vào
     * mất trạng thái sau mỗi lần đổi bộ lọc.
     */
    onCollapsedChange?: (next: boolean) => void
    /** Kết quả (bảng/thẻ) nằm bên phải sidebar. */
    children: ReactNode
}

/**
 * Bố cục danh sách kiểu KiotViet: bộ lọc là cột trái luôn mở (≥ md), kết quả
 * bên phải. Dưới md giữ ô tìm + nút Lọc mở bottom sheet như mọi màn khác.
 *
 * Trên máy bàn, cột lọc ăn 16rem cố định; lúc chỉ cần nhìn bảng thì nút 3 ngạch
 * ngang thu cột lại còn bề rộng một cái nút (M-59). Thu gọn KHÔNG đụng tới bộ
 * lọc đang áp: badge trên nút vẫn đếm số bộ lọc đang bật và hàng chip vẫn nằm
 * trên bảng, để không ai ngồi nhìn một danh sách bị lọc mà tưởng là tất cả.
 */
export function ListFilterSidebarLayout<T>({
    bar,
    panel,
    onSidebarApply,
    collapsed = false,
    onCollapsedChange,
    children,
}: Props<T>) {
    const apply = onSidebarApply ?? bar.onApply
    // Không có chỗ lưu trạng thái thì coi như không thu gọn được.
    const isCollapsed = !!onCollapsedChange && collapsed
    const showCount = isCollapsed && bar.activeCount > 0
    const toggleLabel = isCollapsed
        ? m.common_filter_expand()
        : m.common_filter_collapse()
    // aria-label che hết nội dung nút với trình đọc màn hình, nên con số phải
    // nằm trong chính nhãn — không thì người dùng mù không biết đang bị lọc.
    const toggleAriaLabel = showCount
        ? `${toggleLabel} — ${m.common_filter_active_count({
              count: String(bar.activeCount),
          })}`
        : toggleLabel
    return (
        <div
            className={twJoin(
                'md:grid md:items-start md:gap-4',
                isCollapsed
                    ? 'md:grid-cols-[auto_minmax(0,1fr)]'
                    : 'md:grid-cols-[16rem_minmax(0,1fr)]',
            )}
        >
            <aside
                data-testid="list-filter-sidebar"
                data-collapsed={isCollapsed ? 'true' : 'false'}
                aria-label={m.common_filter()}
                className={twJoin(
                    'bg-card hidden rounded-md border md:block',
                    isCollapsed ? 'p-2' : 'p-4',
                )}
            >
                {onCollapsedChange ? (
                    <div
                        className={twJoin(
                            'flex items-center',
                            isCollapsed ? 'justify-center' : 'mb-3 gap-2',
                        )}
                    >
                        <Button
                            type="button"
                            // `icon` là ô vuông cứng 36px: nhét thêm con số vào
                            // là tràn ra ngoài nút. Có số thì để nút tự giãn.
                            size={showCount ? 'sm' : 'icon'}
                            variant="ghost"
                            data-testid="list-filter-collapse-toggle"
                            aria-label={toggleAriaLabel}
                            aria-expanded={!isCollapsed}
                            // Chỉ trỏ tới panel khi panel còn trong DOM — trỏ
                            // vào một id không tồn tại là tham chiếu ARIA hỏng.
                            aria-controls={isCollapsed ? undefined : PANEL_ID}
                            title={toggleLabel}
                            onClick={() => onCollapsedChange(!isCollapsed)}
                        >
                            <Menu className="size-4" />
                            {/* Thu gọn rồi thì không còn ô lọc nào nhìn thấy —
                                con số này là thứ duy nhất nói "danh sách đang
                                bị lọc". */}
                            {showCount ? (
                                <span className="bg-primary text-primary-foreground rounded-full px-1.5 text-xs font-medium tabular-nums">
                                    {bar.activeCount}
                                </span>
                            ) : null}
                        </Button>
                        {isCollapsed ? null : (
                            <span className="text-sm font-medium">
                                {m.common_filter()}
                            </span>
                        )}
                    </div>
                ) : null}
                {/* Thu gọn = gỡ hẳn khỏi DOM, không ẩn bằng CSS: control còn
                    trong DOM là còn tab focus vào được và còn trùng với bản
                    trong bottom sheet. */}
                {isCollapsed ? null : (
                    <div id={PANEL_ID}>
                        {panel({
                            value: bar.value,
                            patch: (partial) =>
                                apply({ ...bar.value, ...partial }),
                            surface: 'bar',
                        })}
                        <div className="mt-4">
                            <ClearFilterButton
                                active={bar.activeCount > 0}
                                onClear={bar.onClear}
                            />
                        </div>
                    </div>
                )}
            </aside>
            <div className="min-w-0 space-y-3">
                <div className="md:hidden">
                    <ListFilterBar {...bar} mobileOnly>
                        {panel}
                    </ListFilterBar>
                </div>
                <div className="hidden md:block">{bar.search}</div>
                {/* Một bộ chip cho mọi kích thước — hai bộ ẩn/hiện bằng CSS
                    làm trùng nút gỡ chip trong DOM. */}
                <ListFilterChips chips={bar.chips ?? []} />
                {children}
            </div>
        </div>
    )
}
